// useAssetPreloader — preload a fixed list of image URLs and report
// when they're all settled.
//
// ════════════════════════════════════════════════════════════════════
// WHY THIS EXISTS
// ════════════════════════════════════════════════════════════════════
//
// Every player game (Ludo, XO, Chess, Carrom, Rummy) renders a lobby
// that's pixel-heavy: cash tiles, player avatars, trophy/medal,
// settings cog, game logo. On a fresh page load, those images stream
// in over many milliseconds, and the lobby renders BEFORE the bytes
// arrive — so the user sees broken-image placeholders and unstyled
// flashes until each PNG completes. The same happens after entering
// a game cold (no service-worker cache, no warmed disk cache).
//
// This hook is the simplest "wait for all of these images" primitive
// we need. Feed it the list of asset URLs your screen depends on;
// it returns:
//
//   ready    — true once EVERY URL has either loaded OR errored
//   loaded   — count that successfully loaded
//   total    — len(urls)
//   progress — clamped [0..1] for a progress bar
//
// Errors count toward `ready` so a broken URL doesn't pin the lobby
// behind a spinner forever — we'd rather show the lobby with one
// broken image than a permanent loader.
//
// ════════════════════════════════════════════════════════════════════
// IMPLEMENTATION NOTES
// ════════════════════════════════════════════════════════════════════
//
// • Uses `new Image()` not `<link rel="preload">` because Image()
//   triggers actual decode + caches the bitmap in the browser's
//   image cache — when the lobby's <img> renders the same src, it
//   pulls instantly from the decoded cache instead of re-decoding.
//
// • A module-level `Set<string>` remembers URLs that have settled
//   in this session. If the player navigates back and forth between
//   the lobby and the game screen, the second mount sees the same
//   URLs as already-settled and flips `ready=true` synchronously on
//   the first render — no spinner flash for cached assets.
//
// • The settled set is keyed by the absolute URL string, so the same
//   image referenced from two different components dedupes naturally.
//
// • The hook re-runs only when the `urls` array IDENTITY changes.
//   Callers should pass a stable array (defined once at module scope,
//   or wrapped in useMemo) — otherwise every render restarts the
//   preload. The included `useStableImageList` helper takes care of
//   identity-stabilising a list whose contents shouldn't trigger a
//   re-preload.

import { useEffect, useRef, useState, useMemo } from 'react';

// Process-wide cache of URLs that have completed (success OR error)
// at least once. Used to skip the preload entirely on revisits.
const SETTLED = new Set<string>();

export type PreloadStatus = {
  /** True once every url has either loaded or errored. */
  ready: boolean;
  /** Count that successfully loaded (excludes errors). */
  loaded: number;
  /** Total URLs being preloaded. */
  total: number;
  /** Clamped to [0..1]. Drives the progress bar UI. */
  progress: number;
};

/**
 * Preload an array of image URLs. Returns a status object that drives
 * a loader UI. See file header for the contract.
 *
 * `minDelayMs` (default 0) — minimum time the hook stays in
 * `!ready` state. Helpful when callers want the loader to be VISIBLE
 * for at least a heartbeat so a short flash on a fast connection
 * doesn't feel like a glitch. Set to 0 to disable the floor.
 */
export function useAssetPreloader(urls: ReadonlyArray<string>, minDelayMs = 0): PreloadStatus {
  // Compute starting state from the session cache. If every URL is
  // already in SETTLED we begin in `ready=true` and never paint a
  // spinner — important for back/forward navigation within a session.
  const initial = useMemo(() => {
    const total = urls.length;
    let loaded = 0;
    for (const u of urls) if (SETTLED.has(u)) loaded += 1;
    return { ready: loaded === total, loaded, total, progress: total === 0 ? 1 : loaded / total };
  }, [urls]);

  const [status, setStatus] = useState<PreloadStatus>(initial);
  const startedAt = useRef<number>(Date.now());

  useEffect(() => {
    startedAt.current = Date.now();
    if (urls.length === 0) {
      setStatus({ ready: true, loaded: 0, total: 0, progress: 1 });
      return;
    }
    let cancelled = false;
    let settled = 0;
    let loaded = 0;

    const handleOne = (url: string, ok: boolean) => {
      if (cancelled) return;
      SETTLED.add(url);
      settled += 1;
      if (ok) loaded += 1;
      const total = urls.length;
      const ready = settled === total;
      const apply = () => {
        if (cancelled) return;
        setStatus({ ready, loaded, total, progress: total === 0 ? 1 : settled / total });
      };
      if (ready && minDelayMs > 0) {
        const elapsed = Date.now() - startedAt.current;
        const remaining = Math.max(0, minDelayMs - elapsed);
        if (remaining > 0) {
          window.setTimeout(apply, remaining);
          return;
        }
      }
      apply();
    };

    for (const url of urls) {
      if (SETTLED.has(url)) {
        // Don't even create an Image() — treat as instantly settled.
        handleOne(url, true);
        continue;
      }
      const img = new Image();
      // decoding='async' lets the browser decode off the main thread;
      // when the lobby <img> later mounts pointing at the same src,
      // it inherits the decoded bitmap straight from the image cache.
      img.decoding = 'async';
      img.onload  = () => handleOne(url, true);
      img.onerror = () => handleOne(url, false);
      img.src = url;
    }

    return () => { cancelled = true; };
  }, [urls, minDelayMs]);

  return status;
}

/**
 * Helper to flatten a heterogeneous IMG dictionary (strings + arrays
 * of strings) into a single deduped URL list. Useful when the game's
 * IMG const mixes the two — e.g. `cash: ['/games/.../Cash-1.png', ...]`.
 * The list identity is memoised against the dict so callers can pass
 * the result straight to useAssetPreloader without an extra useMemo.
 */
export function flattenImageList(
  dict: Record<string, string | ReadonlyArray<string>>,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const v of Object.values(dict)) {
    if (typeof v === 'string') {
      if (!seen.has(v)) { seen.add(v); out.push(v); }
    } else if (Array.isArray(v)) {
      for (const s of v) if (typeof s === 'string' && !seen.has(s)) { seen.add(s); out.push(s); }
    }
  }
  return out;
}
